import React from "react";
import MaterialTable from "material-table";
import axios from "axios";
import Hashids from "hashids";
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";
import { differenceInCalendarDays, format, isValid, parseISO, subDays } from "date-fns";
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  Chip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import ImageSearchIcon from "@material-ui/icons/ImageSearch";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import { Lightbox } from "react-modal-image";
import AlertSnackBar from "../alertas/AlertSnackBar";
import ConfirmDialog from "../alertas/Confirm";
import PrintDialog from "../alertas/Print";
import { getPermissions } from "../../libs/permissions";
import api from "../../service/api";

const BPF_SERVER_URL = process.env.REACT_APP_BPF_SERVER_URL;
const BPF_SHARE_URL = process.env.REACT_APP_BPF_SHARE_URL;
const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
const MAX_DAYS_RANGE = 40;

// Células com texto longo: largura limitada + reticências (o valor completo
// aparece no tooltip ao passar o mouse). Cabeçalho quebra em linha p/ ficar legível.
const CELL_STYLE = {
  maxWidth: 110,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
// Cabeçalho quebra em palavras (não letra a letra), mantendo as colunas
// estreitas e legíveis — deixa a tabela responsiva sem rolagem.
const HEADER_STYLE = {
  whiteSpace: "normal",
};

const withTooltip = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return (
    <Tooltip title={String(value)}>
      <span>{value}</span>
    </Tooltip>
  );
};

// Persistência da escolha de colunas (mostrar/ocultar) no navegador.
const COLUMNS_STORAGE_KEY = "bpf_forms_table_hidden_columns";

const loadHiddenMap = () => {
  try {
    return JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const saveHiddenField = (field, hidden) => {
  if (!field) return;
  try {
    const map = loadHiddenMap();
    map[field] = hidden;
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    // localStorage indisponível: ignora (não persiste, mas não quebra).
  }
};

// Colunas base da listagem (title/field). O seletor de colunas (menu próprio)
// controla a visibilidade via estado — não usamos o columnsButton nativo do
// material-table, que na v1.69 quebra ao alternar colunas sem width explícita.
const BASE_COLUMNS = [
  { title: "Codigo", field: "id" },
  { title: "Versao", field: "form_version" },
  { title: "Data", field: "date" },
  { title: "Identificador", field: "identifier" },
  { title: "Responsavel", field: "user" },
  { title: "Verificador", field: "checker" },
  { title: "Monitor", field: "supervisor" },
  { title: "Compartilhado", field: "user_share" },
  { title: "Data Compartilhamento", field: "date_sharing" },
  { title: "Data Preenchimento", field: "date_complete" },
  { title: "Ult. Acao", field: "last_action_name" },
];

// Ocultas por padrão (costumam vir vazias); reexibíveis pelo seletor de colunas.
const DEFAULT_HIDDEN = [
  "checker", // Verificador
  "supervisor", // Monitor
  "date_sharing", // Data Compartilhamento
  "date_complete", // Data Preenchimento
  "last_action_name", // Ult. Ação
];

// ---------------------------------------------------------------------------
// Assinatura em lote (Verificar / Monitorar)
//
// São as mesmas duas ações do preenchimento (AddDialogForm), aplicadas de uma
// vez à seleção da listagem via POST /data-form/bulk-review. Os nomes confundem:
// `checker` é o **Verificador** e `supervisor` é o **Monitor** — a coluna do
// banco é essa, o rótulo da tela é o outro.
// ---------------------------------------------------------------------------
const REVIEW_ROLES = {
  checker: {
    label: "Verificar",
    action: "verificar",
    column: "checker",
    // Perfis que assinam — mesma regra dos botões do preenchimento. O corte
    // aqui é só UX; quem decide de verdade é o backend.
    permissions: ["ADMINISTRADOR", "VERIFICADOR", "SUPERVISOR"],
  },
  supervisor: {
    label: "Monitorar",
    action: "monitorar",
    column: "supervisor",
    permissions: ["ADMINISTRADOR", "MONITOR", "SUPERVISOR"],
  },
};

// Compartilhado e ainda não devolvido preenchido: não há o que assinar (mesma
// regra que desabilita o PDF da linha).
const isPendingShare = (rowData) => !rowData.date_complete && !!rowData.date_sharing;

// "Aqueles que podem": falta a assinatura daquele papel e o preenchimento já
// existe de fato. Olha o id **e** o nome porque as duas coisas chegam por
// caminhos diferentes — a busca traz os dois, mas `setMonitor`/`setVerificador`
// (assinatura de um só, pelo diálogo) atualizam apenas o nome.
const canAssign = (rowData, role) =>
  !isPendingShare(rowData) &&
  !rowData[`${role}_id`] &&
  !rowData[REVIEW_ROLES[role].column];

const isSelectable = (rowData) =>
  Object.keys(REVIEW_ROLES).some((role) => canAssign(rowData, role));

// Estado inicial de visibilidade: preferência salva tem precedência sobre o padrão.
const computeInitialHidden = (savedMap = {}) => {
  const map = {};
  BASE_COLUMNS.forEach((c) => {
    map[c.field] =
      savedMap[c.field] !== undefined
        ? savedMap[c.field]
        : DEFAULT_HIDDEN.includes(c.field);
  });
  return map;
};

// Registro criado por automação de botão de ação e ainda não preenchido. O
// cast do model manda boolean; os outros formatos cobrem a coluna crua.
const isPendingFill = (rowData) =>
  rowData.pending_fill === true || rowData.pending_fill === 1 || rowData.pending_fill === "1";

// Monta as colunas aplicando o mapa de visibilidade (field -> hidden).
const buildColumns = (urlSplit, hiddenMap = {}) => {
  const columns = BASE_COLUMNS.map((column) => ({
    ...column,
    hidden: !!hiddenMap[column.field],
    cellStyle: { ...CELL_STYLE },
    headerStyle: { ...HEADER_STYLE },
    render: (rowData) =>
      column.field === "identifier" && isPendingFill(rowData) ? (
        <span>
          <Chip size="small" label="Pendente" color="secondary" style={{ marginRight: 6 }} />
          {withTooltip(rowData[column.field])}
        </span>
      ) : (
        withTooltip(rowData[column.field])
      ),
  }));

  columns.push({ title: "Id Sharing", field: "sharing_id", hidden: true });
  columns.push({
    title: "Checkin",
    field: "checkin",
    hidden: urlSplit[0] !== "heineken",
    cellStyle: { ...CELL_STYLE },
    headerStyle: { ...HEADER_STYLE },
    render: (rowData) => {
      if (!rowData.checkin) {
        return null;
      }
      const utcDate = new Date(rowData.checkin.replace(" ", "T") + "Z");
      return withTooltip(utcDate.toLocaleString());
    },
  });

  return columns;
};

export default class TableOs extends React.PureComponent {
  constructor(props) {
    super(props);

    const url = window.location.hostname;
    const urlSplit = url.split(".");
    const today = format(new Date(), "yyyy-MM-dd");
    const initialFromDate = props.initialFromDate || today;
    const initialToDate = props.initialToDate || today;

    this.unmounted = false;
    this.searchCancelSource = null;
    this.requestCounter = 0;
    this.tableRef = React.createRef();
    this.dataCache = [];

    this.urlSplit = urlSplit;

    this.state = {
      loading: false,
      form_id: props.form_id,
      fromDate: props.fromDate,
      toDate: props.toDate,
      data_filter: props.data_filter || "",
      open: false,
      filename_open: "",
      row: null,
      dataFormId: null,
      openPrintDialog: false,
      permissions: null,
      idDelete: null,
      indexFile: null,
      openDialogConfirmDelete: false,
      title: "",
      body: "",
      hiddenMap: computeInitialHidden(loadHiddenMap()),
      columnMenuAnchor: null,
      // Assinatura em lote: ids marcados (como string, para casar com qualquer
      // origem do id) e o papel aguardando confirmação.
      selectedIds: [],
      bulkRole: null,
      // Papel sendo assinado agora (null = nenhum): controla o spinner do botao.
      bulkLoading: null,
      alert: false,
      alertType: "success",
      alertMessage: "",
    };
  }

  toggleColumn = (field) => {
    const hidden = !this.state.hiddenMap[field];
    const hiddenMap = { ...this.state.hiddenMap, [field]: hidden };
    this.setState({ hiddenMap });
    saveHiddenField(field, hidden);
  };

  componentDidMount() {
    this.getDataForms();
    const permissions = getPermissions();
    if (permissions) {
      this.setState({ permissions: JSON.parse(permissions) });
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.searchCancelSource) {
      this.searchCancelSource.cancel("Componente desmontado");
    }
  }

  normalizeDateRange = (fromDate, toDate) => {
    const today = format(new Date(), "yyyy-MM-dd");
    let safeToDate = toDate;
    let safeFromDate = fromDate;

    const parsedToDate = parseISO(safeToDate);
    if (!safeToDate || !isValid(parsedToDate)) {
      safeToDate = today;
    }

    const parsedFromDate = parseISO(safeFromDate);
    if (!safeFromDate || !isValid(parsedFromDate)) {
      safeFromDate = safeToDate;
    }

    if (safeFromDate > safeToDate) {
      const temp = safeFromDate;
      safeFromDate = safeToDate;
      safeToDate = temp;
    }

    const rangeInDays = differenceInCalendarDays(
      parseISO(safeToDate),
      parseISO(safeFromDate)
    );

    if (rangeInDays > MAX_DAYS_RANGE) {
      safeFromDate = format(subDays(parseISO(safeToDate), MAX_DAYS_RANGE), "yyyy-MM-dd");
    }

    return { fromDate: safeFromDate, toDate: safeToDate };
  };

  reloadTableQuery = () => {
    if (this.tableRef.current) {
      this.tableRef.current.onQueryChange();
    }
  };

  replaceDataCache = (nextData) => {
    this.dataCache = Array.isArray(nextData) ? nextData : [];
    if (this.unmounted) {
      return;
    }
    // Lista nova, seleção zerada: manter marcações de linhas que saíram do
    // filtro assinaria o que o usuário não está mais vendo.
    this.setState({ loading: false, selectedIds: [] }, this.reloadTableQuery);
  };

  updateDataCache = (updater) => {
    const current = Array.isArray(this.dataCache) ? this.dataCache : [];
    this.dataCache = updater(current);
    this.reloadTableQuery();
  };

  searchForms = (data_filter, fromDate, toDate) => {
    // const normalizedRange = this.normalizeDateRange(fromDate, toDate);
    this.setState(
      {
        data_filter,
        fromDate: fromDate,
        toDate: toDate,
      },
      this.getDataForms
    );
  };

  getUserNameFromCookie = () => {
    const nameCookie = Cookies.get("name_bpf");
    if (!nameCookie) {
      return "";
    }
    return CryptoJS.AES.decrypt(nameCookie, SECRET_KEY).toString(CryptoJS.enc.Utf8);
  };

  setMonitor = (data_form_id) => {
    const name = this.getUserNameFromCookie();
    this.updateDataCache((rows) =>
      rows.map((item) =>
        String(item.id) === String(data_form_id) ? { ...item, supervisor: name } : item
      )
    );
  };

  setVerificador = (data_form_id) => {
    const name = this.getUserNameFromCookie();
    this.updateDataCache((rows) =>
      rows.map((item) =>
        String(item.id) === String(data_form_id) ? { ...item, checker: name } : item
      )
    );
  };

  /* ---------------------- Assinatura em lote ---------------------- */

  // A seleção vive no estado (não no dataCache) e é reaplicada às linhas em
  // `resolveTableData` — assim ela sobrevive à paginação e à ordenação, que no
  // material-table remoto refazem a query a cada mudança.
  hasPermission = (role) =>
    REVIEW_ROLES[role].permissions.includes(this.state.permissions?.name);

  canBulkReview = () => Object.keys(REVIEW_ROLES).some(this.hasPermission);

  selectedRows = () => {
    const rows = Array.isArray(this.dataCache) ? this.dataCache : [];
    const selected = this.state.selectedIds;
    return rows.filter((row) => selected.includes(String(row.id)));
  };

  // Quantos da seleção o papel realmente assina (a linha pode estar marcada
  // por causa do outro papel).
  eligibleSelectedIds = (role) =>
    this.selectedRows()
      .filter((row) => canAssign(row, role))
      .map((row) => row.id);

  toggleSelection = (rowData) => {
    const id = String(rowData.id);
    const selectedIds = this.state.selectedIds.includes(id)
      ? this.state.selectedIds.filter((item) => item !== id)
      : [...this.state.selectedIds, id];

    this.setState({ selectedIds }, this.reloadTableQuery);
  };

  // Marca/desmarca tudo que o filtro atual trouxe e que ainda pode ser assinado
  // — inclusive o que está em outras páginas, que é o ponto do lote.
  toggleSelectAll = () => {
    const selectable = (Array.isArray(this.dataCache) ? this.dataCache : []).filter(
      isSelectable
    );
    const allSelected =
      selectable.length > 0 && selectable.length === this.state.selectedIds.length;

    this.setState(
      { selectedIds: allSelected ? [] : selectable.map((row) => String(row.id)) },
      this.reloadTableQuery
    );
  };

  clearSelection = () => {
    this.setState({ selectedIds: [] }, this.reloadTableQuery);
  };

  openBulkConfirm = (role) => {
    this.setState({ bulkRole: role });
  };

  closeBulkConfirm = () => {
    this.setState({ bulkRole: null });
  };

  confirmBulkReview = () => {
    const role = this.state.bulkRole;
    const ids = this.eligibleSelectedIds(role);

    if (ids.length === 0) {
      this.setState({ bulkRole: null });
      return;
    }

    // Fecha a confirmacao ja: o progresso aparece no botao do lote, e o dialogo
    // parado esperando a resposta pareceria travado.
    this.setState({ bulkLoading: role, bulkRole: null });

    api
      .post("/data-form/bulk-review", {
        form_id: this.state.form_id,
        role,
        ids,
      })
      .then((response) => {
        // Em erro de rede o interceptor de api.js resolve com undefined em vez
        // de rejeitar; sem esta guarda o .then quebraria ao ler .data.
        if (!response) {
          this.setState({
            bulkLoading: null,
            bulkRole: null,
            alert: true,
            alertType: "error",
            alertMessage: "Falha de comunicacao com o servidor.",
          });
          return;
        }

        this.applyBulkResult(role, response.data.data.result);
      })
      .catch((error) => {
        this.setState({
          bulkLoading: null,
          bulkRole: null,
          alert: true,
          alertType: "error",
          alertMessage:
            error?.data?.message ||
            error?.data?.error ||
            error?.message ||
            "Nao foi possivel assinar os preenchimentos.",
        });
      });
  };

  // Pinta as linhas assinadas sem recarregar a lista (o backend devolve quem
  // assinou) e mostra a coluna do papel, que vem oculta por padrao — senao o
  // usuario nao ve o resultado da acao que acabou de fazer.
  applyBulkResult = (role, result) => {
    const { column } = REVIEW_ROLES[role];
    const assigned = (result.assigned || []).map(String);
    const skipped = result.skipped || [];
    const reviewer = result.reviewer?.name || this.getUserNameFromCookie();

    this.dataCache = (Array.isArray(this.dataCache) ? this.dataCache : []).map((row) =>
      assigned.includes(String(row.id))
        ? { ...row, [column]: reviewer, [`${role}_id`]: result.reviewer?.id }
        : row
    );

    const hiddenMap = { ...this.state.hiddenMap, [column]: false };
    saveHiddenField(column, false);

    const message =
      skipped.length > 0
        ? `${assigned.length} preenchimento(s) assinado(s). ${skipped.length} ignorado(s) (ja assinados ou ainda nao preenchidos).`
        : `${assigned.length} preenchimento(s) assinado(s).`;

    this.setState(
      {
        hiddenMap,
        selectedIds: [],
        bulkLoading: null,
        bulkRole: null,
        alert: true,
        alertType: assigned.length > 0 ? "success" : "warning",
        alertMessage: message,
      },
      this.reloadTableQuery
    );
  };

  getDataForms = () => {
    // const normalizedRange = this.normalizeDateRange(this.state.fromDate, this.state.toDate);
    // if (
    //   normalizedRange.fromDate !== this.state.fromDate ||
    //   normalizedRange.toDate !== this.state.toDate
    // ) {
    //   this.setState(
    //     {
    //       fromDate: normalizedRange.fromDate,
    //       toDate: normalizedRange.toDate,
    //     },
    //     this.getDataForms
    //   );
    //   return;
    // }

    if (this.searchCancelSource) {
      this.searchCancelSource.cancel("Nova consulta iniciada");
    }

    this.searchCancelSource = axios.CancelToken.source();
    const requestId = ++this.requestCounter;

    this.setState({ loading: true });
    api
      .get("/data-form/search", {
        params: {
          form_id: this.state.form_id,
          data: this.state.data_filter == "" ? null : this.state.data_filter,
          fromDate: this.state.fromDate,
          toDate: this.state.toDate,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        cancelToken: this.searchCancelSource.token,
      })
      .then((response) => {
        if (this.unmounted || requestId !== this.requestCounter) {
          return;
        }
        if (!response) {
          this.setState({ loading: false });
          return;
        }
        this.replaceDataCache(response.data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        if (this.unmounted || requestId !== this.requestCounter) {
          return;
        }
        this.setState({ loading: false });
      });
  };

  addFiles = (file) => {
    const rowId = this.state.row?.id;
    if (rowId === null || rowId === undefined) {
      return;
    }

    this.updateDataCache((rows) =>
      rows.map((item) => {
        if (String(item.id) !== String(rowId)) {
          return item;
        }
        const files = Array.isArray(item.files) ? item.files : [];
        return { ...item, files: [...files, file] };
      })
    );
    this.setState({ row: null });
  };

  removeFiles = (indexFile) => {
    const rowId = this.state.row?.id;
    if (rowId === null || rowId === undefined) {
      return;
    }

    this.updateDataCache((rows) =>
      rows.map((item) => {
        if (String(item.id) !== String(rowId)) {
          return item;
        }
        const files = Array.isArray(item.files) ? item.files : [];
        return {
          ...item,
          files: files.map((file, idx) =>
            idx === indexFile ? { ...file, status: "0" } : file
          ),
        };
      })
    );

    this.setState({ row: null, indexFile: null });
  };

  // Imagem abre no Lightbox (que renderiza <img>); qualquer outro tipo vai para
  // uma aba nova, onde o Content-Type de /showFile decide se abre inline (PDF)
  // ou baixa.
  modalView = (filename) => {
    const ext = (filename || "").split(".").pop().toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);

    if (isImage) {
      this.setState({ open: true, filename_open: filename });
    } else {
      window.open(
        `${BPF_SERVER_URL}/showFile?filename=${encodeURIComponent(filename)}`
      );
    }
  };

  removeRows = () => {
    this.setState({ row: null });
  };

  closeLightbox = () => {
    this.setState({ open: false });
  };

  downloadImagem = (nomeArquivo) => {
    window.open(`${BPF_SERVER_URL}/downloadFile?filename=${nomeArquivo}`);
  };

  openShare = (rowData) => {
    const hashids = new Hashids();
    const url = `${BPF_SHARE_URL}/?form=${hashids.encode(this.state.form_id)}&sharing=${hashids.encode(rowData.sharing_id)}&data_form=${hashids.encode(rowData.id)}`;
    window.open(url);
  };

  deleteImage = (id, indexFile) => {
    this.setState({
      idDelete: id,
      indexFile,
      openDialogConfirmDelete: true,
      title: "Atencao",
      body: "Deseja deletar o arquivo?",
    });
  };

  handleDelete = () => {
    api
      .delete(`/filesDataForm/${this.state.idDelete}`)
      .then(() => {
        this.setState({ idDelete: null, openDialogConfirmDelete: false });
        this.removeFiles(this.state.indexFile);
      })
      .catch(() => {
        this.setState({ idDelete: null, openDialogConfirmDelete: false });
      });
  };

  handleClose = () => {
    this.setState({ idDelete: null, openDialogConfirmDelete: false });
  };

  handlePrintV = () => {
    if (this.state.dataFormId !== null && this.state.dataFormId !== undefined) {
      window.open(
        `${BPF_SERVER_URL}/generate-pdf/${this.state.dataFormId}?timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
      );
    }
    this.setState({ dataFormId: null, openPrintDialog: false });
  };

  handlePrintH = () => {
    if (this.state.dataFormId !== null && this.state.dataFormId !== undefined) {
      window.open(
        `${BPF_SERVER_URL}/generate-pdf/${this.state.dataFormId}?pgOrientation=landscape&timezone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
      );
    }
    this.setState({ dataFormId: null, openPrintDialog: false });
  };

  getActions = () => {
    const actions = [
      {
        icon: "add",
        tooltip: "Add",
        isFreeAction: true,
        onClick: () => this.props.add(),
      },
      {
        icon: "share",
        tooltip: "Compartilhar",
        isFreeAction: true,
        onClick: () => this.props.share(),
      },
      {
        icon: "edit",
        tooltip: "Editar Dados",
        onClick: (event, rowData) => this.props.edit(rowData.id, rowData.is_editable),
      },
      (rowData) => ({
        icon: "description",
        tooltip: "PDF",
        onClick: (event, rowDataCurrent) => {
          const url = window.location.hostname;
          const urlSplit = url.split(".");
          if (urlSplit[0] === "sbr") {
            this.setState({
              dataFormId: rowDataCurrent.id,
              openPrintDialog: true,
            });
          } else {
            window.open(`${BPF_SERVER_URL}/generate-pdf/${rowDataCurrent.id}`);
          }
        },
        disabled:
          (rowData.date_complete === null || rowData.date_complete === undefined) &&
          rowData.date_sharing !== null &&
          rowData.date_sharing !== undefined,
      }),
      {
        icon: "attach_file",
        tooltip: "Anexos",
        onClick: (event, rowData) => {
          this.setState({ row: rowData });
          this.props.attach(rowData.id);
        },
      },
      (rowData) => ({
        icon: "share",
        tooltip: "Link Compartilhado",
        onClick: () => this.openShare(rowData),
        disabled: rowData.date_sharing === null || rowData.date_sharing === undefined,
      }),
      (rowData) => ({
        icon: "backup",
        tooltip: "Logs",
        onClick: () => this.props.logs(rowData.id),
      }),
    ];

    if (this.state.permissions?.name === "ADMINISTRADOR") {
      actions.push({
        icon: "feedback",
        tooltip: "Solicitar edicao",
        onClick: (event, rowData) => this.props.audit(rowData.id, rowData.is_editable),
      });
    }

    return actions;
  };

  resolveTableData = (query) => {
    const sourceRows = Array.isArray(this.dataCache) ? this.dataCache : [];
    let rows = sourceRows;

    if (query.orderBy && query.orderDirection) {
      const field = query.orderBy.field;
      const order = query.orderDirection === "asc" ? 1 : -1;
      rows = [...sourceRows].sort((a, b) => {
        const aValue = a?.[field];
        const bValue = b?.[field];

        if (aValue === bValue) {
          return 0;
        }
        if (aValue === null || aValue === undefined) {
          return -1 * order;
        }
        if (bValue === null || bValue === undefined) {
          return 1 * order;
        }
        return aValue > bValue ? order : -1 * order;
      });
    }

    const pageSize = query.pageSize || 25;
    const page = query.page || 0;
    const start = page * pageSize;
    const end = start + pageSize;

    // A marcacao mora no estado, nao na linha: reaplicada aqui, ela sobrevive a
    // troca de pagina e de ordenacao (que refazem esta consulta).
    const selected = this.state.selectedIds;

    return Promise.resolve({
      data: rows.slice(start, end).map((row) => ({
        ...row,
        __selected: selected.includes(String(row.id)),
      })),
      page,
      totalCount: rows.length,
    });
  };

  renderDetailPanel = (rowData) => {
    const files = Array.isArray(rowData.files) ? rowData.files : [];
    return (
      <Paper
        margin={1}
        style={{
          boxShadow: "1px 3px 1px #ddd",
          padding: "10px",
          margin: "10px",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          component="div"
          style={{
            fontSize: "1rem",
            color: "#fff",
            backgroundColor: "#256F44",
            textAlign: "center",
            borderRadius: "5px",
            padding: "10px",
          }}
        >
          INFORMACOES DOS ARQUIVOS
        </Typography>
        <Table size="small" aria-label="purchases">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome do Arquivo</TableCell>
              <TableCell align="center">Data Criacao</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {files.map((file, indexFile) => (
              <TableRow key={file.id || `${file.filename}-${indexFile}`}>
                <TableCell align="center" component="th" scope="row">
                  {file.filename}
                </TableCell>
                <TableCell align="center">{file.created_at}</TableCell>
                <TableCell align="center">{file.status === "1" ? "Ativo" : "Inativo"}</TableCell>
                <TableCell align="center">
                  <IconButton
                    disabled={file.status === "0"}
                    aria-label="view"
                    onClick={() => this.modalView(file.filename)}
                  >
                    <ImageSearchIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    disabled={file.status === "0"}
                    aria-label="download"
                    onClick={() => this.downloadImagem(file.filename)}
                  >
                    <GetAppIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    disabled={file.status === "0"}
                    aria-label="delete"
                    onClick={() => {
                      this.setState({ row: rowData });
                      this.deleteImage(file.id, indexFile);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  };

  // Coluna de marcação do lote. Linha sem nada a assinar (já verificada e já
  // monitorada, ou compartilhamento em aberto) fica desabilitada, com o motivo
  // no tooltip — é o "aqueles que podem" da tela.
  renderSelectionColumn = () => ({
    title: "Sel.",
    field: "__selected",
    sorting: false,
    cellStyle: { width: 48, padding: "0 0 0 8px" },
    headerStyle: { ...HEADER_STYLE, width: 48, padding: "0 0 0 8px" },
    render: (rowData) => {
      const disabled = !isSelectable(rowData);
      const reason = isPendingShare(rowData)
        ? "Compartilhado e ainda nao preenchido"
        : "Ja verificado e monitorado";

      return (
        <Tooltip title={disabled ? reason : "Selecionar para verificar/monitorar"}>
          <span>
            <Checkbox
              size="small"
              color="primary"
              disabled={disabled}
              checked={!!rowData.__selected}
              onChange={() => this.toggleSelection(rowData)}
            />
          </span>
        </Tooltip>
      );
    },
  });

  renderBulkBar = () => {
    const { selectedIds, bulkLoading } = this.state;
    const selectable = (Array.isArray(this.dataCache) ? this.dataCache : []).filter(
      isSelectable
    );
    const allSelected =
      selectable.length > 0 && selectedIds.length === selectable.length;

    return (
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
        <Tooltip title="Selecionar todos os elegiveis do filtro">
          <span>
            <Checkbox
              size="small"
              color="primary"
              disabled={selectable.length === 0}
              checked={allSelected}
              indeterminate={selectedIds.length > 0 && !allSelected}
              onChange={this.toggleSelectAll}
            />
          </span>
        </Tooltip>
        <Typography variant="body2" style={{ marginRight: 12 }}>
          {selectedIds.length} selecionado(s)
        </Typography>
        {Object.keys(REVIEW_ROLES)
          .filter(this.hasPermission)
          .map((role) => {
            const eligible = this.eligibleSelectedIds(role).length;
            return (
              <Button
                key={role}
                size="small"
                variant="contained"
                color="secondary"
                style={{ marginRight: 8 }}
                disabled={eligible === 0 || bulkLoading !== null}
                onClick={() => this.openBulkConfirm(role)}
              >
                {bulkLoading === role && (
                  <CircularProgress size={16} style={{ color: "#fff", marginRight: 6 }} />
                )}
                {REVIEW_ROLES[role].label} ({eligible})
              </Button>
            );
          })}
        {selectedIds.length > 0 && (
          <Button size="small" onClick={this.clearSelection}>
            Limpar
          </Button>
        )}
      </div>
    );
  };

  renderBulkConfirm = () => {
    const role = this.state.bulkRole;
    const eligible = this.eligibleSelectedIds(role).length;
    const ignored = this.state.selectedIds.length - eligible;

    return (
      <ConfirmDialog
        title="Atencao"
        body={
          `Confirmar ${REVIEW_ROLES[role].action} ${eligible} preenchimento(s) como ` +
          `${this.getUserNameFromCookie()}?` +
          (ignored > 0
            ? ` ${ignored} da selecao ja tem esta assinatura e sera(ao) ignorado(s).`
            : "") +
          " A assinatura nao pode ser desfeita."
        }
        handleCloseConfirmT={this.confirmBulkReview}
        handleCloseConfirmF={this.closeBulkConfirm}
      />
    );
  };

  render() {
    const bulkEnabled = this.canBulkReview();
    const columns = buildColumns(this.urlSplit, this.state.hiddenMap);

    if (bulkEnabled) {
      columns.unshift(this.renderSelectionColumn());
    }

    return (
      <div>
        {this.state.alert && (
          <AlertSnackBar
            open={this.state.alert}
            type={this.state.alertType}
            message={this.state.alertMessage}
            onClose={() => this.setState({ alert: false })}
          />
        )}
        {this.state.bulkRole !== null && this.renderBulkConfirm()}
        {this.state.open && (
          <Lightbox
            large={`${BPF_SERVER_URL}/showFile?filename=${this.state.filename_open}`}
            alt={this.state.filename_open}
            onClose={this.closeLightbox}
          />
        )}
        {this.state.openDialogConfirmDelete && (
          <ConfirmDialog
            title={this.state.title}
            body={this.state.body}
            handleCloseConfirmT={this.handleDelete}
            handleCloseConfirmF={this.handleClose}
          />
        )}
        {this.state.openPrintDialog && (
          <PrintDialog
            handleCloseConfirmH={this.handlePrintH}
            handleCloseConfirmV={this.handlePrintV}
          />
        )}

        {/* Barra do lote (à esquerda) + seletor de colunas próprio, que evita o
            columnsButton bugado do material-table. */}
        <div
          style={{
            display: "flex",
            justifyContent: bulkEnabled ? "space-between" : "flex-end",
            alignItems: "center",
            padding: "4px 8px 0",
          }}
        >
          {bulkEnabled && this.renderBulkBar()}
          <Tooltip title="Mostrar/ocultar colunas">
            <IconButton
              size="small"
              onClick={(e) => this.setState({ columnMenuAnchor: e.currentTarget })}
            >
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={this.state.columnMenuAnchor}
            open={Boolean(this.state.columnMenuAnchor)}
            onClose={() => this.setState({ columnMenuAnchor: null })}
          >
            {BASE_COLUMNS.map((column) => (
              <MenuItem
                key={column.field}
                onClick={() => this.toggleColumn(column.field)}
                dense
              >
                <Checkbox
                  edge="start"
                  checked={!this.state.hiddenMap[column.field]}
                  tabIndex={-1}
                  disableRipple
                />
                {column.title}
              </MenuItem>
            ))}
          </Menu>
        </div>

        <MaterialTable
          key={JSON.stringify(this.state.hiddenMap)}
          tableRef={this.tableRef}
          isLoading={this.state.loading}
          columns={columns}
          data={this.resolveTableData}
          title={this.props.name}
          options={{
            search: false,
            pageSize: 5,
            pageSizeOptions: [5, 15, 25, 50, 100],
            emptyRowsWhenPaging: false,
            debounceInterval: 500,
          }}
          actions={this.getActions()}
          detailPanel={[
            {
              render: this.renderDetailPanel,
            },
          ]}
        />
      </div>
    );
  }
}
